import React, { useState } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { useMutation } from 'react-query';

import { digitalCertificatesService } from '@project/services';
import { FileUpload, ProgressBar, TailwindButton } from '@project/components';
import { checkIdentifierCsv, IdentifierCsvCheck, MAX_IDENTIFIERS } from './identifier-csv';

const FALLBACK_FILE_NAME = 'certificates.zip';

const fileNameFromHeaders = (headers: any): string => {
  const disposition = headers?.['content-disposition'];
  const match = disposition && /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (!match?.[1]) {
    return FALLBACK_FILE_NAME;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const saveZip = (data: Blob, headers: any): void => {
  const anchorElement = document.createElement('a');
  const url = URL.createObjectURL(new Blob([data], { type: 'application/zip' }));
  anchorElement.href = url;
  anchorElement.setAttribute('download', fileNameFromHeaders(headers));
  anchorElement.click();
  anchorElement.remove();
  URL.revokeObjectURL(url);
};

const readErrorMessage = async (error: any): Promise<string> => {
  const response = error?.response;

  if (!response) {
    return 'Could not reach the server. Please try again.';
  }

  if (response.status === 401 || response.status === 403) {
    return 'You do not have permission to generate digital certificates.';
  }

  let payload = response.data;
  if (payload instanceof Blob) {
    try {
      payload = JSON.parse(await payload.text());
    } catch {
      payload = null;
    }
  }

  const errors = payload?.errors;
  if (errors) {
    const messages = Object.keys(errors).reduce<string[]>(
      (all, key) => all.concat(errors[key] ?? []),
      [],
    );
    if (messages.length) {
      return messages.join(' ');
    }
  }

  return payload?.title ?? 'An error occurred while generating certificates.';
};

export const DigitalCertificatesBulkGenerateScreen: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [csvCheck, setCsvCheck] = useState<IdentifierCsvCheck | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [hasGenerated, setHasGenerated] = useState<boolean>(false);

  const onChangeDropZoneFile = async (inputFile: File) => {
    setFile(inputFile);
    setCsvCheck(null);
    setUploadProgress(null);
    setHasGenerated(false);
    setCsvCheck(await checkIdentifierCsv(inputFile));
  };

  const generateMutation = useMutation(
    'bulkGenerateDigitalCertificates',
    () => {
      setUploadProgress(0);
      return digitalCertificatesService.bulkGenerate(file, setUploadProgress);
    },
    {
      onSuccess: (apiResponse) => {
        saveZip(apiResponse.data, apiResponse.headers);
        toast.success('Certificates generated');
        setFile(null);
        setCsvCheck(null);
        setUploadProgress(null);
        setHasGenerated(true);
      },
      onError: async (error) => {
        toast.error(await readErrorMessage(error));
        setUploadProgress(null);
      },
    },
  );

  const isReadyToGenerate = !!file && csvCheck?.isValid === true;

  const csvCheckMessage = () => {
    if (!csvCheck) {
      return null;
    }

    if (!csvCheck.isValid) {
      return (
        <div className="mt-3 text-sm text-red-600">
          {'message' in csvCheck ? csvCheck.message : 'The CSV file is invalid.'}
        </div>
      );
    }

    const { identifierColumn, uniqueCount, blankCount, duplicateCount } = csvCheck.summary;
    const dropped = [
      blankCount ? `${blankCount} blank` : null,
      duplicateCount ? `${duplicateCount} duplicate` : null,
    ].filter(Boolean);

    return (
      <div className="mt-3 text-sm text-gray-600">
        <p>
          {uniqueCount} of {MAX_IDENTIFIERS} identifiers, read from the &quot;{identifierColumn}
          &quot; column.
        </p>
        {!!dropped.length && (
          <p className="text-yellow-700">
            {dropped.join(' and ')} {dropped.length > 1 ? 'rows' : 'row'} will be skipped.
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <Toaster />
      <div className="flex items-center justify-center flex-col">
        <div className="my-5 text-lg">Bulk Generate Digital Certificates</div>
        <div className="flex justify-center items-center w-full">
          <FileUpload onChange={onChangeDropZoneFile} file={file} />
        </div>
        {csvCheckMessage()}
        {uploadProgress !== null && (
          <div className="mt-4 w-1/2">
            <ProgressBar
              progress={uploadProgress}
              label={uploadProgress < 100 ? 'Uploading file' : 'Generating certificates'}
            />
          </div>
        )}
        {hasGenerated && (
          <div className="mt-4 max-w-2xl p-3 text-sm border-l-4 border-yellow-400 bg-yellow-50 text-yellow-800">
            The download only means the request completed &mdash; individual rows can still have
            failed. Open <span className="font-semibold">manifest.csv</span> inside the zip to see
            the outcome and reason for every identifier.
          </div>
        )}
        <div className="m-3">
          <TailwindButton
            className="bg-tertiary hover:bg-tertiary-dark focus:bg-tertiary-dark self-center"
            isDisabled={!isReadyToGenerate}
            isLoading={generateMutation.isLoading}
            onClick={() => generateMutation.mutate()}
            type="submit"
          >
            Generate {file?.name ?? ''}
          </TailwindButton>
        </div>
      </div>
    </>
  );
};
